import i18n from "../../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { isOperationalAnimalField } from "@/lib/livestockFieldVisibility";

const DEFAULT_STATUS_OPTIONS = ["active", "pregnant", "sick", "ready_for_sale", "sold", "transferred", "culled", "deceased"];
const DEFAULT_SOURCE_OPTIONS = ["born_on_farm", "purchased", "rescued", "gifted", "transferred_in"];
const DEFAULT_SEX_OPTIONS = ["male", "female", "unknown"];

function DynamicField({ field, value, onChange }) {
    if (field.field_type === "number") {
        return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-11" />;
    }
    if (field.field_type === "date") {
        return <Input type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-11" />;
    }
    if (field.field_type === "boolean") {
        return (
            <Select value={String(value === true)} onValueChange={(v) => onChange(v === "true")}>
                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="true">{i18n.t("batch2.5397e0583f14")}</SelectItem>
                    <SelectItem value="false">{i18n.t("batch2.816c52fd2bdd")}</SelectItem>
                </SelectContent>
            </Select>
        );
    }
    if (field.field_type === "dropdown") {
        const options = Array.isArray(field.options) ? field.options : [];
        return (
            <Select value={value ?? ""} onValueChange={onChange}>
                <SelectTrigger className="h-11"><SelectValue placeholder={i18n.t("batch2.349ac8fb67f7")} /></SelectTrigger>
                <SelectContent>
                    {options.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                </SelectContent>
            </Select>
        );
    }
    return <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-11" />;
}

function buildInitialState(initial, species) {
    return {
        species_id: initial?.species_id || species?.id || "",
        tag_id: initial?.tag_id || "",
        name: initial?.name || "",
        breed: initial?.breed || "",
        sex: initial?.sex || "unknown",
        birth_date: initial?.birth_date || "",
        acquisition_date: initial?.acquisition_date || "",
        acquisition_type: initial?.acquisition_type || "",
        status: initial?.status || "active",
        pen: initial?.pen || "",
        assigned_area: initial?.assigned_area || initial?.location || "",
        purchase_price: initial?.purchase_price ?? "",
        photo_url: initial?.photo_url || "",
        notes: initial?.notes || "",
        custom_fields: initial?.custom_fields || {},
    };
}

export default function AnimalForm({ speciesOptions = [], species, fields = [], initial = null, onSaved, onCancel, onSpeciesChange }) {
    const [form, setForm] = useState(buildInitialState(initial, species));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(buildInitialState(initial, species));
    }, [initial?.id, species?.id]);

    const breedOptions = Array.isArray(species?.breed_options) ? species.breed_options : [];
    const statusOptions = Array.isArray(species?.status_options) && species.status_options.length ? species.status_options : DEFAULT_STATUS_OPTIONS;
    const sourceOptions = Array.isArray(species?.source_options) && species.source_options.length ? species.source_options : DEFAULT_SOURCE_OPTIONS;
    const sexOptions = Array.from(new Set([
        ...(Array.isArray(species?.sex_options) && species.sex_options.length ? species.sex_options : DEFAULT_SEX_OPTIONS),
        "unknown",
    ]));
    const areaOptions = Array.isArray(species?.location_labels) ? species.location_labels : [];

    const groupedFields = useMemo(() => {
        const groups = {};
        (fields || []).filter((field) => isOperationalAnimalField(field, species)).forEach((field) => {
            const key = field.section || "Custom";
            groups[key] = groups[key] || [];
            groups[key].push(field);
        });
        return groups;
    }, [fields, species]);

    const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));

    const submit = async (event) => {
        event.preventDefault();
        if (!form.species_id) {
            toast.error("Species is required");
            return;
        }
        if (!form.tag_id.trim()) {
            toast.error("Tag or animal ID is required");
            return;
        }
        setSaving(true);
        const payload = {
            species_id: form.species_id,
            tag_id: form.tag_id.trim(),
            name: form.name.trim(),
            breed: form.breed.trim(),
            sex: form.sex,
            birth_date: form.birth_date || null,
            acquisition_date: form.acquisition_date || null,
            acquisition_type: form.acquisition_type,
            status: form.status,
            pen: form.pen.trim(),
            assigned_area: form.assigned_area.trim(),
            purchase_price: form.purchase_price === "" ? null : Number(form.purchase_price),
            photo_url: form.photo_url.trim(),
            notes: form.notes,
            custom_fields: form.custom_fields,
        };
        try {
            if (initial?.id) {
                await api.put(`/animals/${initial.id}`, payload);
            } else {
                await api.post("/animals", payload);
            }
            toast.success(initial?.id ? "Animal updated" : "Animal created");
            onSaved?.();
        } catch (error) {
            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to save animal");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-6" data-testid="universal-animal-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.83a7238826ab")}</Label>
                    <Select value={form.species_id || "__none__"} onValueChange={(value) => {
                        const nextValue = value === "__none__" ? "" : value;
                        setValue("species_id", nextValue);
                        onSpeciesChange?.(nextValue);
                    }}>
                        <SelectTrigger className="mt-2 h-11"><SelectValue placeholder={i18n.t("batch2.5c173ef78781")} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">{i18n.t("batch2.5c173ef78781")}</SelectItem>
                            {speciesOptions.map((item) => <SelectItem key={item.id} value={item.id}>{item.icon ? `${item.icon} ` : ""}{item.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.96f84c4f4b2d")}</Label>
                    <Input value={form.tag_id} onChange={(e) => setValue("tag_id", e.target.value)} className="mt-2 h-11" required />
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.709a23220f2c")}</Label>
                    <Input value={form.name} onChange={(e) => setValue("name", e.target.value)} className="mt-2 h-11" />
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.777c078c9b7b")}</Label>
                    <Input value={form.breed} onChange={(e) => setValue("breed", e.target.value)} className="mt-2 h-11" list="animal-breed-options" />
                    {breedOptions.length ? <datalist id="animal-breed-options">{breedOptions.map((option) => <option key={option} value={option} />)}</datalist> : null}
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.e301dd6062f7")}</Label>
                    <Select value={form.sex} onValueChange={(value) => setValue("sex", value)}>
                        <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                        <SelectContent>{sexOptions.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.bae7d5be7082")}</Label>
                    <Select value={form.status} onValueChange={(value) => setValue("status", value)}>
                        <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                        <SelectContent>{statusOptions.map((option) => <SelectItem key={option} value={option}>{option.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.a6989f1caddd")}</Label>
                    <Input type="date" value={form.birth_date} onChange={(e) => setValue("birth_date", e.target.value)} className="mt-2 h-11" />
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.29bbaabc2052")}</Label>
                    <Input type="date" value={form.acquisition_date} onChange={(e) => setValue("acquisition_date", e.target.value)} className="mt-2 h-11" />
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.6da13addb000")}</Label>
                    <Select value={form.acquisition_type || "__none__"} onValueChange={(value) => setValue("acquisition_type", value === "__none__" ? "" : value)}>
                        <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">{i18n.t("batch2.60f1e913db61")}</SelectItem>
                            {sourceOptions.map((option) => <SelectItem key={option} value={option}>{option.replace(/_/g, " ")}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.4c8f26e7117b")}</Label>
                    <Input value={form.pen} onChange={(e) => setValue("pen", e.target.value)} className="mt-2 h-11" placeholder={i18n.t("batch2.64b69a9e55a0")} />
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.a65de8bce998")}</Label>
                    <Input value={form.assigned_area} onChange={(e) => setValue("assigned_area", e.target.value)} className="mt-2 h-11" list="animal-area-options" />
                    {areaOptions.length ? <datalist id="animal-area-options">{areaOptions.map((option) => <option key={option} value={option} />)}</datalist> : null}
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.747876156da0")}</Label>
                    <Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setValue("purchase_price", e.target.value)} className="mt-2 h-11" />
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch2.84dfdb55f9e6")}</Label>
                    <Input value={form.photo_url} onChange={(e) => setValue("photo_url", e.target.value)} className="mt-2 h-11" placeholder={i18n.t("batch2.1a66279d9648")} />
                </div>
                <div className="md:col-span-2">
                    <Label className="tiny-label">{i18n.t("batch2.70440046a3dc")}</Label>
                    <Textarea value={form.notes} onChange={(e) => setValue("notes", e.target.value)} className="mt-2 min-h-[100px]" />
                </div>
            </div>

            {Object.keys(groupedFields).length ? (
                <div className="space-y-4">
                    {Object.entries(groupedFields).map(([section, sectionFields]) => (
                        <div key={section}>
                            <div className="tiny-label mb-3">{section}</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {sectionFields.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((field) => (
                                    <div key={field.id}>
                                        <Label className="tiny-label">{field.label}</Label>
                                        <div className="mt-2">
                                            <DynamicField
                                                field={field}
                                                value={form.custom_fields?.[field.field_key]}
                                                onChange={(value) => setForm((current) => ({
                                                    ...current,
                                                    custom_fields: { ...(current.custom_fields || {}), [field.field_key]: value },
                                                }))}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}

            <div className="flex gap-3 pt-4 border-t border-border">
                <Button type="button" variant="outline" className="h-11" onClick={onCancel}>{i18n.t("batch2.77dfd2135f4d")}</Button>
                <Button type="submit" className="h-11 flex-1" disabled={saving}>{saving ? "Saving…" : initial?.id ? "Save Changes" : "Create Animal"}</Button>
            </div>
        </form>
    );
}
